import React, { useEffect, useMemo, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import FiltroMultiple from "../../Postventa/Dashboard/Modales/FiltroMultiple";
import { CategoriaMaquinariaArray } from "../../../Arrays/CategoriaMaquinariaArray";

const ESTADO_VACIO_FILTROS = { categoria: [], tipo: [], marca: [], anio: [] };
const ESTADO_VACIO_MOSTRAR = {
  categoria: { open: false },
  tipo: { open: false },
  marca: { open: false },
  anio: { open: false },
};

// El backend manda categoria como código corto ("JD"/"OTRA_MARCA") — se traduce a texto legible
const textoCategoria = (v) => CategoriaMaquinariaArray.find((c) => c.id === v)?.display || v;

// Tabla agrupada por tipo — colapsable, mismo patrón que ReporteGeolocalizacionTabla/
// ReporteClientesTabla (Reportes CRM): click en la fila de grupo despliega/contrae sus
// máquinas; el contador de máquinas queda a la derecha de la fila de grupo. "listado" llega
// plano — una fila por máquina con idmaquina, categoria, tipo, marca, modelo, serie, anio,
// comentarios.
export const ParqueMaquinariaTabla = ({ listado: listadoProp, onEditar }) => {
  const listado = Array.isArray(listadoProp) ? listadoProp : [];
  const [options, setOptions] = useState(ESTADO_VACIO_FILTROS);
  const [filtros, setFiltros] = useState(ESTADO_VACIO_FILTROS);
  const [filtrosTemp, setFiltrosTemp] = useState(ESTADO_VACIO_FILTROS);
  const [mostrarFiltro, setMostrarFiltro] = useState(ESTADO_VACIO_MOSTRAR);
  const [prioridadFiltros, setPrioridadFiltros] = useState([]);
  const [abiertos, setAbiertos] = useState({});

  const onToggle = (tipo) => {
    setAbiertos((prev) => ({ ...prev, [tipo]: !prev[tipo] }));
  };

  useEffect(() => {
    setOptions({
      categoria: [...new Set(listado.map((m) => m.categoria).filter(Boolean))].map((v) => ({ value: v, label: textoCategoria(v) })),
      tipo: [...new Set(listado.map((m) => m.tipo).filter(Boolean))].map((v) => ({ value: v, label: v })),
      marca: [...new Set(listado.map((m) => m.marca).filter(Boolean))].map((v) => ({ value: v, label: v })),
      anio: [...new Set(listado.map((m) => m.anio).filter(Boolean))].map((v) => ({ value: v, label: v })),
    });
  }, [listado]);

  const actualizarPrioridad = (filtro) => {
    setPrioridadFiltros((prev) => {
      const index = prev.indexOf(filtro);
      const nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

      setFiltrosTemp((prevTemp) => {
        const limpio = { ...prevTemp };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      setFiltros((prevFiltros) => {
        const limpio = { ...prevFiltros };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      return nuevasPrioridades;
    });
  };

  const tiposAgrupados = useMemo(() => {
    let data = [...listado];

    prioridadFiltros.forEach((filtro) => {
      if (filtro === "categoria" && filtros.categoria.length > 0) {
        data = data.filter((m) => filtros.categoria.includes(m.categoria));
      }
      if (filtro === "tipo" && filtros.tipo.length > 0) {
        data = data.filter((m) => filtros.tipo.includes(m.tipo));
      }
      if (filtro === "marca" && filtros.marca.length > 0) {
        data = data.filter((m) => filtros.marca.includes(m.marca));
      }
      if (filtro === "anio" && filtros.anio.length > 0) {
        data = data.filter((m) => filtros.anio.includes(m.anio));
      }
    });

    const grupos = new Map();
    data.forEach((m) => {
      const clave = m.tipo || "Sin tipo";
      if (!grupos.has(clave)) {
        grupos.set(clave, { tipo: clave, maquinas: [] });
      }
      grupos.get(clave).maquinas.push(m);
    });

    return [...grupos.values()].sort((a, b) => a.tipo.localeCompare(b.tipo, "es"));
  }, [listado, filtros, prioridadFiltros]);

  const totalMaquinas = tiposAgrupados.reduce((acc, g) => acc + g.maquinas.length, 0);

  const filtroProps = (field, label) => ({
    label,
    field,
    options: options[field],
    filtros,
    setFiltros,
    filtrosTemp,
    setFiltrosTemp,
    mostrarFiltro,
    setMostrarFiltro,
    actualizarPrioridad,
    setPrioridadFiltros,
  });

  return (
    <div className="crmcs-cot-tabla-wrap">
      <div className="crmcs-cot-tabla-scroll scroll">
        <table cellPadding={0} cellSpacing={0} className="paq-maq-tabla">
          <thead>
            <tr>
              <th>
                <div>
                  <FiltroMultiple {...filtroProps("tipo", "Tipo")} />
                </div>
              </th>
              <th className="paq-maq-th-centro">
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("categoria", "Categoría")} />
                </div>
              </th>
              <th className="paq-maq-th-centro">
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("marca", "Marca")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">Modelo</div>
              </th>
              <th>
                <div className="borde-izq">Serie</div>
              </th>
              <th className="paq-maq-th-centro">
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("anio", "Año")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">Comentarios</div>
              </th>
              <th className="paq-maq-th-centro">
                <div className="borde-izq">.</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {tiposAgrupados.map((grupo) => {
              const expandido = !!abiertos[grupo.tipo];
              return (
                <React.Fragment key={grupo.tipo}>
                  <tr className="rca-row-asesor" onClick={() => onToggle(grupo.tipo)}>
                    <td className="rca-td-asesor">
                      <span className="rca-flecha">{expandido ? "▼" : "▶"}</span>
                      <strong>{grupo.tipo}</strong>
                    </td>
                    <td colSpan={5}></td>
                    <td className="rca-td-contador" colSpan={2}>
                      {grupo.maquinas.length} equipo{grupo.maquinas.length === 1 ? "" : "s"}
                    </td>
                  </tr>

                  {expandido && grupo.maquinas.map((m) => (
                    <tr key={m.idmaquina} className="rca-row-cliente">
                      <td></td>
                      <td className="paq-maq-td-centro">{textoCategoria(m.categoria)}</td>
                      <td className="paq-maq-td-centro">{m.marca}</td>
                      <td>{m.modelo}</td>
                      <td>{m.serie}</td>
                      <td className="paq-maq-td-centro">{m.anio || "—"}</td>
                      <td className="paq-maq-td-comentarios">{m.comentarios || "—"}</td>
                      <td className="paq-maq-td-centro">
                        <button type="button" className="btn-row-menu" onClick={() => onEditar?.(m)}>
                          <EditIcon fontSize="small" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="crmcs-cot-tabla-total">
        <span>Total ({tiposAgrupados.length} tipos)</span>
        <span>{totalMaquinas} equipos</span>
      </div>
    </div>
  );
};
